import React, { useMemo, useState } from 'react';
import {
  Modal,
  Input,
  Form,
  Tooltip,
  Select,
  Spin,
  Checkbox,
  Row,
  Col,
  Collapse,
  Switch,
  message,
  Tabs,
  Divider,
  Space,
  Button,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { getProviderFromUrl } from '../../../../../../Utils/providerURL';
import {
  useCompaniesData,
  useEventNames,
  useSendTelegramTestMessage,
  useUpdateMultipleCompanies,
} from '../../../../../../Hooks/TT_ELD/Companies';
import { useProjectData } from '../../../../../../Hooks/useProjectData';
import {
  useDailiesFindEmails,
  useDriverCompanyFinderQuery,
} from '../../../../../../Hooks/TT_ELD/Dailies';
import { useUserCompaniesData } from '../../../../../../Hooks/TT_ELD/UserCompanies';
import { debounce } from 'lodash';
import { cameraEnabled } from '../../../../../../Utils/cameraEnabled';
import { isWhiteLabelAdmin } from '../../../../../../Utils/whiteLabel';

interface MultipleEditModalProps {
  visible: boolean;
  onClose: () => void;
}

type EventNamesData = {
  [key: string]: {
    title: string;
  };
};

const MultipleEditModal: React.FC<MultipleEditModalProps> = ({ visible, onClose }) => {
  const [form] = Form.useForm();

  const isWhiteLabel = isWhiteLabelAdmin();

  const [searchEmail, setSearchEmail] = useState('');
  const [selectedDriverId, setSelectedDriverId] = useState<string | undefined>(undefined);

  const [searchCompany, setSearchCompany] = useState('');

  const { data: emailsResponse, isLoading: emailsLoading } = useDailiesFindEmails(searchEmail);

  const { data: searchedCompanies, isLoading: companySearchLoading } =
    useDriverCompanyFinderQuery(searchCompany);

  const { projectData } = useProjectData();

  const { mutate: senMessage, isLoading: isPending } = useSendTelegramTestMessage();

  const provider = getProviderFromUrl(projectData?.baseUrl);
  const [searchValue, setSearchValue] = useState('');

  const { mutate, isLoading } = useUpdateMultipleCompanies();

  const { data: companiesResponse, isLoading: companiesLoading } = useUserCompaniesData(
    undefined,
    undefined,
    undefined,
    selectedDriverId,
    1,
  );

  const companyOptions = useMemo(() => {
    if (selectedDriverId && companiesResponse?.data) {
      const companies = companiesResponse.data
        .filter((item: any) => item.company)
        .map((item: any) => ({
          label: item.company.name,
          value: item.company.id,
        }));

      return companies.length ? [{ label: 'All', value: '__ALL__' }, ...companies] : [];
    }

    if (!selectedDriverId && searchedCompanies) {
      return searchedCompanies.map((company: any) => ({
        label: company.name,
        value: company.id,
      }));
    }

    return [];
  }, [selectedDriverId, companiesResponse, searchedCompanies]);

  const handleCompanyChange = (values: (string | number)[]) => {
    if (values.includes('__ALL__')) {
      form.setFieldsValue({
        companyIds: ['__ALL__'],
      });
    } else {
      form.setFieldsValue({
        companyIds: values,
      });
    }
  };

  const groups = {
    'Harsh Driving': ['rapid-acc', 'rapid-dec', 'harsh-turn'],
    Crash: ['collision'],
    'Distracted Driving': [
      'distracted-driving',
      'phone-calls',
      'fatigue-driving',
      'overtime-fatigue-driving',
    ],
    'Policy Violations': ['no-seatbelt'],
    Speeding: ['overspeed'],
    Other: ['sos-emergency', 'low-vehicle-battery'],
  };

  const formatGroupedOptions = (data: EventNamesData = {}) => {
    const grouped: { label: string; options: { value: string; label: string }[] }[] = [];

    const groupedKnown = Object.entries(groups).map(([groupName, keys]) => ({
      label: groupName,
      options: keys
        .filter((key) => key in data)
        .map((key) => ({
          value: key,
          label: data[key].title,
        })),
    }));

    grouped.push(...groupedKnown);

    const knownKeys = Object.values(groups).flat();
    const unknownKeys = Object.keys(data).filter((key) => !knownKeys.includes(key));

    if (unknownKeys.length > 0) {
      grouped.push({
        label: 'Other',
        options: unknownKeys.map((key) => ({
          value: key,
          label: data[key].title,
        })),
      });
    }

    return grouped;
  };

  const { data: eventNamesData } = useEventNames();
  const groupedOptions = formatGroupedOptions(eventNamesData);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      const payload: any = {
        companyIds: values.companyIds,
      };

      if (values.companyIds?.includes('__ALL__')) {
        payload.isAll = true;
        payload.userId = selectedDriverId;
        payload.companyIds = [];
      } else {
        payload.companyIds = values.companyIds;
      }

      // ===== Chat IDs =====
      if (values.chatId) {
        payload.chatId = values.chatId;
      }

      if (values.notificationChatId) {
        payload.notificationChatId = values.notificationChatId;
      }

      if (values.notificationEldFaultChatId) {
        payload.notificationEldFaultChatId = values.notificationEldFaultChatId;
      }

      if (values.notificationEldDisconnectGlobalChatId) {
        payload.notificationEldDisconnectGlobalChatId =
          values.notificationEldDisconnectGlobalChatId;
      }

      if (values.cameraAlertChatId) {
        payload.cameraAlertChatId = values.cameraAlertChatId;
      }

      // ===== Camera Alert Settings =====
      if (values.settings && Object.keys(values.settings).length > 0) {
        payload.settings = values.settings;
      }

      // ===== Config =====
      if (values.config && Object.keys(values.config).length > 0) {
        payload.config = values.config;
      }

      mutate(payload, {
        onSuccess: () => {
          message.success('Updated successfully!');
          form.resetFields();
          onClose();
        },
        onError: (err: any) => {
          message.error(err?.response?.data?.error?.message || 'Update failed');
        },
      });
    } catch (error: any) {
      if (error?.errorFields?.length) {
        message.error(error.errorFields[0].errors[0]);
      } else {
        message.error('Validation error occurred');
      }
    }
  };

  // Violations test message
  const handleTestMessage = () => {
    const chatId = form.getFieldValue('chatId');

    if (!chatId) {
      return;
    }

    const payload = {
      chatId,
      botType: `eld`,
      message: 'Success! Your chat has been connected, and notifications will now be sent here',
    };

    senMessage(payload);
  };

  // Notfiaction tab test message

  const handleNotificationTestMessage = () => {
    const chatId = form.getFieldValue('notificationChatId');

    if (!chatId) {
      return;
    }

    const payload = {
      chatId,
      botType: `eld`,
      message: 'Success! Your chat has been connected, and notifications will now be sent here',
    };

    senMessage(payload);
  };

  // Global notfication test message

  const handleEldFaultTestMessage = () => {
    const chatId = form.getFieldValue('notificationEldFaultChatId');

    if (!chatId) return;

    senMessage({
      chatId,
      botType: 'faultcodes',
      message: 'Success! Your chat has been connected, and notifications will now be sent here',
    });
  };

  const handleGlobalDisconnectTestMessage = () => {
    const chatId = form.getFieldValue('notificationEldDisconnectGlobalChatId');

    if (!chatId) return;

    senMessage({
      chatId,
      botType: 'disconnect',
      message: 'Success! Your chat has been connected, and notifications will now be sent here',
    });
  };

  // Camera alerts test message

  const handleCameraAlertTestMessage = () => {
    const chatId = form.getFieldValue('cameraAlertChatId');

    if (!chatId) return;

    senMessage({
      chatId,
      botType: 'protrux',
      message: 'Success! Your chat has been connected, and notifications will now be sent here',
    });
  };

  const debouncedSearchEmail = useMemo(
    () =>
      debounce((value: string) => {
        setSearchEmail(value);
      }, 400),
    [],
  );

  const debouncedSearchCompany = useMemo(
    () =>
      debounce((value: string) => {
        setSearchCompany(value);
      }, 400),
    [],
  );

  return (
    <Modal
      title="Add Chat ID"
      open={visible}
      onOk={handleOk}
      onCancel={onClose}
      okText="Add"
      confirmLoading={isLoading}
      width={700}
    >
      <Form form={form} layout="vertical">
        {/* Driver Email Search */}
        <Form.Item name="isAll" initialValue={false} hidden>
          <Input />
        </Form.Item>
        <Form.Item label="Email" name="driverEmail">
          <Select
            allowClear
            showSearch
            placeholder="Search by email"
            filterOption={false}
            loading={emailsLoading}
            onSearch={debouncedSearchEmail}
            onChange={(value) => {
              setSelectedDriverId(value);
              form.setFieldsValue({ companyIds: [] });
            }}
            notFoundContent={emailsLoading ? <Spin size="small" /> : 'No emails found'}
            options={emailsResponse?.map((user: any) => ({
              label: user?.email,
              value: user?.id,
            }))}
          />
        </Form.Item>
        {/* ================= Companies ================= */}
        <Form.Item
          label="Companies"
          name="companyIds"
          rules={[{ required: true, message: 'Please select at least one company' }]}
        >
          <Select
            mode="multiple"
            allowClear
            showSearch
            filterOption={false}
            placeholder="Select companies"
            options={companyOptions}
            onChange={handleCompanyChange}
            onSearch={debouncedSearchCompany}
            className="custom-select"
          />
        </Form.Item>

        <Tabs defaultActiveKey="1">
          {/* ================= 1. Violations ================= */}
          <Tabs.TabPane tab="Violations" key="1">
            <Form.Item
              label={
                <span>
                  Violations Chat ID{' '}
                  <span style={{ color: '#999' }}>
                    {`(Supported bots: @control_${provider}_bot)`}
                  </span>
                </span>
              }
              noStyle={false}
            >
              <Space.Compact style={{ width: '100%' }}>
                <Form.Item name="chatId" noStyle>
                  <Input placeholder="Enter Chat ID" style={{ flex: 1 }} />
                </Form.Item>

                <Button type="primary" loading={isPending} onClick={handleTestMessage}>
                  Test Message
                </Button>
              </Space.Compact>
            </Form.Item>
          </Tabs.TabPane>

          {/* ================= 2. Notifications ================= */}
          <Tabs.TabPane tab="Notifications" key="2">
            <Form.Item
              wrapperCol={{ span: '100%' }}
              label={
                <span>
                  Notification Chat ID{' '}
                  <span style={{ color: '#999' }}>
                    {`(Supported bots: @control_${provider}_bot)`}
                  </span>
                </span>
              }
            >
              <Space.Compact style={{ width: '100%' }}>
                <Form.Item name="notificationChatId" noStyle>
                  <Input placeholder="Enter Chat ID" />
                </Form.Item>
                <Button type="primary" loading={isPending} onClick={handleNotificationTestMessage}>
                  Test Message
                </Button>
              </Space.Compact>
            </Form.Item>

            <>
              <Divider />
              <Form.Item
                label="Notification Type"
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.config !== currentValues.config
                }
              >
                {() => {
                  return (
                    <>
                      <Form.Item
                        name={['config', 'notificationWeighStation']}
                        valuePropName="checked"
                        noStyle
                      >
                        <Checkbox>Weigh Station</Checkbox>
                      </Form.Item>
                      <Form.Item
                        name={['config', 'notificationEldDisconnect']}
                        valuePropName="checked"
                        noStyle
                      >
                        <Checkbox style={{ marginLeft: 24 }}>ELD Disconnect</Checkbox>
                      </Form.Item>
                      <Form.Item
                        name={['config', 'notificationEldFaultByCompany']}
                        valuePropName="checked"
                        noStyle
                      >
                        <Checkbox style={{ marginLeft: 24 }}>Truck Fault Code</Checkbox>
                      </Form.Item>
                    </>
                  );
                }}
              </Form.Item>
            </>
          </Tabs.TabPane>

          {/* ================= 3. Global Notifications ================= */}
          {!isWhiteLabel && (
            <Tabs.TabPane tab="Global Notifications" key="3">
              <Form.Item
                wrapperCol={{ span: '100%' }}
                label={
                  <span>
                    Truck Fault Code Chat ID{' '}
                    <span style={{ color: '#999' }}>(Supported bots: @eld_faultcodes_bot)</span>
                  </span>
                }
              >
                <Space.Compact style={{ width: '100%' }}>
                  <Form.Item noStyle name="notificationEldFaultChatId">
                    <Input placeholder="Enter Chat ID" />
                  </Form.Item>
                  <Button type="primary" loading={isPending} onClick={handleEldFaultTestMessage}>
                    Test Message
                  </Button>
                </Space.Compact>
              </Form.Item>

              <Form.Item
                wrapperCol={{ span: '100%' }}
                label={
                  <span>
                    Global ELD Disconnection Chat ID{' '}
                    <span style={{ color: '#999' }}>(Supported bots: @eld_disconnect_bot)</span>
                  </span>
                }
                name="notificationEldDisconnectGlobalChatId"
              >
                <Space.Compact style={{ width: '100%' }}>
                  <Form.Item noStyle name="notificationEldDisconnectGlobalChatId">
                    <Input placeholder="Enter Chat ID" />
                  </Form.Item>
                  <Button
                    type="primary"
                    loading={isPending}
                    onClick={handleGlobalDisconnectTestMessage}
                  >
                    Test Message
                  </Button>
                </Space.Compact>
              </Form.Item>

              <>
                <Divider />
                <Form.Item
                  label="Notification Type"
                  shouldUpdate={(prevValues, currentValues) =>
                    prevValues.config !== currentValues.config
                  }
                >
                  {() => {
                    return (
                      <>
                        <Form.Item
                          name={['config', 'notificationEldFault']}
                          valuePropName="checked"
                          noStyle
                        >
                          <Checkbox>Truck Fault Code</Checkbox>
                        </Form.Item>
                        <Form.Item
                          name={['config', 'notificationEldDisconnectGlobal']}
                          valuePropName="checked"
                          noStyle
                        >
                          <Checkbox style={{ marginLeft: 24 }}>Global ELD Disconnect</Checkbox>
                        </Form.Item>
                      </>
                    );
                  }}
                </Form.Item>
              </>
            </Tabs.TabPane>
          )}

          {/* ================= 4. Alerts ================= */}
          {cameraEnabled(projectData?.projectName) ||
            (!isWhiteLabel && (
              <Tabs.TabPane tab="Alerts" key="4">
                <Form.Item
                  label={
                    <span>
                      Camera Alert Chat ID{' '}
                      <span style={{ color: '#999' }}>(Supported bots: @protrux_dashcam_bot)</span>
                    </span>
                  }
                >
                  <Space.Compact style={{ width: '100%' }}>
                    <Form.Item noStyle name="cameraAlertChatId">
                      <Input placeholder="Enter Chat ID" />
                    </Form.Item>
                    <Button
                      type="primary"
                      loading={isPending}
                      onClick={handleCameraAlertTestMessage}
                    >
                      Test Message
                    </Button>
                  </Space.Compact>
                </Form.Item>

                <Collapse defaultActiveKey={[groupedOptions[0]?.label]}>
                  {groupedOptions.map((group) => (
                    <Collapse.Panel header={group.label} key={group.label}>
                      {group.options.map((item) => (
                        <Form.Item
                          key={item.value}
                          name={['settings', item.value]}
                          label={item.label}
                          valuePropName="checked"
                          style={{ marginBottom: 8 }}
                        >
                          <Switch />
                        </Form.Item>
                      ))}
                    </Collapse.Panel>
                  ))}
                </Collapse>
              </Tabs.TabPane>
            ))}
        </Tabs>
      </Form>
    </Modal>
  );
};

export default MultipleEditModal;
